import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Gift, Sparkles, Star } from "lucide-react";

export type PrizeDto = {
  PrizeId: number;
  code: string;
  name: string;
  type: string;
  valueJson: string;
};

type Props = {
  count?: number;
  onPick: () => Promise<PrizeDto>;
  onDone?: (p: PrizeDto) => void;
};

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function RewardStat(props: { label: string; value: string }) {
  return (
    <div className="rounded-[18px] border border-black/10 bg-white/55 px-4 py-3">
      <p className="text-[11px] font-medium tracking-[0.08em] text-black/44">
        {props.label}
      </p>
      <p className="mt-2 text-sm font-medium text-black/78">{props.value}</p>
    </div>
  );
}

export default function GiftBoxGame({ count = 9, onPick, onDone }: Props) {
  const { t } = useTranslation();

  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const [opening, setOpening] = useState(false);
  const [result, setResult] = useState<PrizeDto | null>(null);

  const grid = useMemo(() => {
    return Array.from({ length: count }, (_, i) => i);
  }, [count]);

  const handlePick = async (idx: number) => {
    if (opening || result) return;

    setPickedIndex(idx);
    setOpening(true);

    try {
      const prize = await onPick();

      window.setTimeout(() => {
        setResult(prize);
        setOpening(false);
        onDone?.(prize);
      }, 700);
    } catch {
      setOpening(false);
      setPickedIndex(null);
    }
  };

  const reset = () => {
    setPickedIndex(null);
    setResult(null);
    setOpening(false);
  };

  return (
    <div className="text-center text-brand-brown">
      {/* HEAD */}
      <div className="mb-5">
        <div className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white/58 px-4 py-2 backdrop-blur-sm">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.03]">
            <Gift className="h-4 w-4 text-brand-gold" />
          </span>
          <span className="text-[11px] font-medium tracking-[0.1em] text-black/62">
            {t("voucherGame.giftBox.kicker")}
          </span>
        </div>

        <h3 className="mt-5 text-[22px] font-medium leading-[1.02] tracking-[0.01em] text-brand-brown sm:text-[26px]">
          {t("voucherGame.giftBox.title")}
        </h3>

        <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-black/54">
          Chọn một hộp quà để mở phần thưởng ngẫu nhiên của bạn.
        </p>
      </div>

      {/* BOARD */}
      <div className="mx-auto max-w-[460px] rounded-[28px] border border-black/10 bg-white/50 p-4 backdrop-blur-sm sm:p-5">
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {grid.map((idx) => {
            const isPicked = pickedIndex === idx;
            const isDisabled = opening || !!result;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handlePick(idx)}
                disabled={isDisabled}
                aria-label={t("voucherGame.giftBox.pickBoxAria", { n: idx + 1 })}
                className={cn(
                  "group relative flex h-[92px] items-center justify-center rounded-[24px] border transition-all duration-300 sm:h-[102px]",
                  isDisabled
                    ? "cursor-not-allowed opacity-75"
                    : "hover:-translate-y-1 hover:bg-white/72",
                  isPicked
                    ? "border-black/22 bg-white/78 shadow-[0_18px_40px_rgba(0,0,0,0.08)]"
                    : "border-black/12 bg-white/42",
                )}
              >
                <div className="absolute left-3 top-3 rounded-full border border-black/8 bg-white/60 px-2.5 py-1 text-[9px] font-medium tracking-[0.08em] text-black/48">
                  {String(idx + 1).padStart(2, "0")}
                </div>

                <div
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-2xl border border-black/10 bg-white/65 transition-transform duration-300",
                    isPicked ? "scale-105" : "group-hover:scale-105",
                  )}
                >
                  <Gift className="h-6 w-6 text-brand-gold" />
                </div>

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-[24px] bg-gradient-to-t from-black/[0.04] to-transparent" />

                {opening && isPicked && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-[24px] bg-white/72 backdrop-blur-sm">
                    <span className="h-6 w-6 rounded-full border-2 border-black/15 border-t-black/60 animate-spin" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {!result && (
          <div className="mt-5 flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-gold" />
            <p className="text-[11px] font-medium tracking-[0.08em] text-black/42">
              {opening
                ? t("voucherGame.giftBox.opening")
                : t("voucherGame.giftBox.tapToOpen")}
            </p>
          </div>
        )}

        {result && (
          <div className="mt-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-green-500/15 bg-green-500/5 px-4 py-2 text-[11px] font-medium tracking-[0.08em] text-green-700">
              <CheckCircle2 className="h-4 w-4" />
              {t("voucherGame.giftBox.rewardKicker")}
            </div>

            <div className="mt-4 rounded-[24px] border border-black/10 bg-white/62 p-5 backdrop-blur-sm">
              <p className="text-sm leading-7 text-black/56">
                {t("voucherGame.giftBox.youGot")}
              </p>

              <p className="mt-3 text-[20px] font-medium leading-8 tracking-[0.01em] text-brand-brown">
                {result.name}
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <RewardStat label="Type" value={result.type} />
                <RewardStat label="Code" value={result.code} />
              </div>

              <button
                type="button"
                onClick={reset}
                className="mt-5 inline-flex h-12 w-full items-center justify-center gap-3 rounded-full border border-black/14 bg-white/62 px-6 text-[11px] font-medium tracking-[0.08em] text-black/70 transition hover:bg-white/82"
              >
                <Star className="h-4 w-4 text-brand-gold" />
                {t("voucherGame.giftBox.playAgain")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}